import type { MouseEvent } from 'react';
import type { useNewsChat } from './use-news-chat';
import { trackUmami } from '@/lib/umami/umami';
import { ChatSuggestions } from './ChatSuggestions';
import { ChatThread } from './ChatThread';

type NewsPanelProps = { news: ReturnType<typeof useNewsChat> };

// Where "Product updates" and "More updates" lead: the marketing site's news page.
export const PRODUCT_UPDATES_URL = 'https://pertento.ai/news/';

/**
 * The docked panel's content: the announcements chat under a "Product updates"
 * label, with a link to the full list in the footer.
 *
 * The label is an h2. The form's own heading is the page's h1, and this is a
 * promotional block beside it, not the page's subject.
 *
 * Link clicks are counted on the way through rather than by a handler on every link:
 * a reply's link card is drawn by the shared ChatBubble, which the signup chat uses too.
 * The panel's own two links carry `data-updates-page`, and any other link is a reply's,
 * inside a wrapper that carries its announcement's id.
 * `display: contents` keeps the wrapper out of the panel's flex layout.
 */
export const NewsPanel = ({ news }: NewsPanelProps) => {
  const countLinkClick = (ev: MouseEvent<HTMLDivElement>) => {
    const link = (ev.target as Element).closest('a');
    if (!link) return;
    // A reply's link names its own announcement: the visitor may have moved on to the next
    // one and scrolled back up to click it.
    const owner = link.closest<HTMLElement>('[data-announcement]')?.dataset['announcement'];
    trackUmami('updates-link-clicked', {
      announcement: owner ?? news.announcementKey,
      link: link.hasAttribute('data-updates-page') ? 'updates-page' : 'reply',
    });
  };

  return (
    <div className="contents" onClickCapture={countLinkClick}>
      <div className="flex justify-end">
        <h2 className="m-0">
          <a
            href={PRODUCT_UPDATES_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-updates-page=""
            className="micro-label hover:text-foreground no-underline transition-colors duration-150 hover:no-underline"
          >
            Product updates
          </a>
        </h2>
      </div>

      <ChatThread thread={news.chat.thread} typing={news.chat.typing}>
        <ChatSuggestions suggestions={news.suggestions} inset />
      </ChatThread>

      <div className="border-t border-[var(--accentLine)] pt-4 text-right">
        <a
          href={PRODUCT_UPDATES_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-updates-page=""
          className="text-dim hover:text-foreground text-micro no-underline transition-colors duration-150 hover:no-underline"
        >
          More updates →
        </a>
      </div>
    </div>
  );
};
